import React, { useCallback, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { downscaleImage } from "@/lib/imageResize";

// ---------------- Data hook ----------------
function useCoverActions(project, onChange) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const _run = useCallback(async (setter, fn, successMsg) => {
    setter(true);
    try {
      const data = await fn();
      if (data) onChange(data);
      if (successMsg) toast.success(successMsg);
      return data;
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setter(false); }
  }, [onChange]);

  // Single upload path — client-side downscale (max 1600×2200, JPEG q=0.85),
  // then POST to /project/cover/upload which now persists to object storage
  // instead of embedding as a base64 data URL. Existing base64 covers on
  // legacy projects continue to render — we don't migrate them automatically.
  const upload = useCallback(async (file) => {
    if (!file) return;
    await _run(setUploading, async () => {
      const small = await downscaleImage(file, { maxWidth: 1600, maxHeight: 2200, quality: 0.85 });
      const originalKB = Math.round(file.size / 1024);
      const smallKB = Math.round(small.size / 1024);
      const fd = new FormData();
      fd.append("file", small);
      const { data } = await api.post("/ph360/project/cover/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const saved = originalKB - smallKB;
      const suffix = saved > 20
        ? ` (${smallKB} KB after downscale — saved ${saved} KB)`
        : ` (${smallKB} KB)`;
      toast.success(`Cover uploaded${suffix}`);
      return data;
    });
  }, [_run]);

  const generate = useCallback(async ({ prompt, genre }) => {
    toast.info("Generating cover with Nano Banana… 10–30 seconds.");
    await _run(setBusy, async () => {
      const { data } = await api.post("/ph360/project/cover/generate", {
        prompt: prompt || null, genre: genre || null,
      });
      return data;
    }, "AI cover generated");
  }, [_run]);

  const remove = useCallback(async () => {
    if (!window.confirm("Remove the title-page cover image?")) return;
    await _run(setBusy, async () => {
      const { data } = await api.delete("/ph360/project/cover");
      return data;
    }, "Cover removed");
  }, [_run]);

  return { busy, uploading, upload, generate, remove };
}

// ---------------- Sub-components ----------------
function CoverPreview({ cover, enabled, source }) {
  const sourceLabel = source === "ai" ? "AI (Nano Banana)"
    : source === "storage" ? "Uploaded to object storage"
    : source === "upload" ? "Uploaded to object storage"
    : source || "Set";
  const isLegacyBase64 = cover.startsWith("data:");
  return (
    <div style={{flex:"0 0 180px"}} data-testid="cover-preview">
      {cover ? (
        <img src={cover} alt="Book cover" style={{width:180, aspectRatio:"3/4", objectFit:"cover", border:"1px solid #c3c4c7", background:"#fff"}} />
      ) : (
        <div style={{width:180, aspectRatio:"3/4", border:"2px dashed #c3c4c7", background:"#fff",
          display:"grid", placeItems:"center", color:"#8c8f94", fontSize:12, textAlign:"center", padding:12}}>
          No cover<br/>set yet
        </div>
      )}
      {enabled && source && (
        <p className="description" style={{marginTop:6}} data-testid="cover-source-badge">
          Source: <strong>{sourceLabel}</strong>
          {isLegacyBase64 && <span style={{display:"block", fontSize:10, color:"#8c8f94"}}>Legacy inline (base64) — re-upload to move to storage</span>}
        </p>
      )}
    </div>
  );
}

function UploadButton({ cover, uploading, busy, onUpload, onRemove }) {
  const fileRef = useRef(null);
  return (
    <>
      <p style={{margin:"0 0 4px", fontWeight:600, fontSize:13}}>Cover image</p>
      <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
        <button className="button button-primary" onClick={()=>fileRef.current?.click()}
          disabled={uploading || busy} data-testid="cover-upload-btn"
          title="Client-side downscale then upload to object storage — PDFs stay compact">
          {uploading ? "Uploading…" : "⬆ Upload cover"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}}
          onChange={(e)=>onUpload(e.target.files?.[0]).finally(()=>{ if (fileRef.current) fileRef.current.value = ""; })}
          data-testid="cover-file-input"/>
        {cover && (
          <button className="button" onClick={onRemove} disabled={busy} data-testid="cover-remove-btn">Remove</button>
        )}
      </div>
      <p className="description" style={{marginTop:6, fontSize:11}}>
        Cover is downscaled in your browser to a max of 1600 × 2200 px, JPEG q=85%, before it
        leaves your machine — then stored in object storage. The exported PDF references the
        stored URL, so PDFs stay small no matter how large the source image was.
      </p>
    </>
  );
}

function AiPromptForm({ initialPrompt, busy, onGenerate }) {
  const [prompt, setPrompt] = useState(initialPrompt || "");
  const [genre, setGenre] = useState("");
  return (
    <div style={{marginTop:14, padding:12, border:"1px solid #dcdcde", background:"#fff"}}>
      <p style={{margin:"0 0 8px", fontWeight:600}}>Generate with AI (Nano Banana)</p>
      <label style={{display:"block", fontSize:12, color:"#5c5c58"}}>Genre / mood (optional)</label>
      <input type="text" value={genre} onChange={(e)=>setGenre(e.target.value)}
        placeholder="e.g. epic fantasy, memoir, business" data-testid="cover-genre-input" />
      <label style={{display:"block", fontSize:12, color:"#5c5c58", marginTop:8}}>Extra instructions (optional)</label>
      <textarea rows={3} value={prompt} onChange={(e)=>setPrompt(e.target.value)}
        placeholder="e.g. dark blue palette, moonlight over an ocean, no people"
        data-testid="cover-prompt-input" />
      <p style={{marginTop:8}}>
        <button className="button button-primary" onClick={()=>onGenerate({prompt, genre})} disabled={busy} data-testid="cover-generate-btn">
          {busy ? "Generating…" : "✨ Generate AI cover"}
        </button>
      </p>
    </div>
  );
}

// ---------------- Component ----------------
export default function CoverSection({ project, onChange }) {
  const cover = project.layout?.title_page_cover_url || "";
  const enabled = !!project.layout?.title_page_cover_enabled;
  const source = project.layout?.title_page_cover_source || "";
  const initialPrompt = (project.layout && project.layout.title_page_cover_prompt) || "";

  const { busy, uploading, upload, generate, remove } = useCoverActions(project, onChange);

  return (
    <div className="ef360-card-inner" data-testid="cover-section"
      style={{marginTop:24, padding:18, background:"#f6f7f7", border:"1px solid #dcdcde"}}>
      <h3 style={{marginTop:0}}>Title-Page Cover Image</h3>
      <p className="description" style={{margin:"4px 0 12px"}}>
        Shown on the title page. Upload a JPG/PNG (auto-downscaled + moved to object storage) or
        let AI illustrate one from your book info.
      </p>
      <div style={{display:"flex", gap:20, alignItems:"flex-start", flexWrap:"wrap"}}>
        <CoverPreview cover={cover} enabled={enabled} source={source} />
        <div style={{flex:"1 1 320px"}}>
          <UploadButton
            cover={cover} uploading={uploading} busy={busy}
            onUpload={upload} onRemove={remove}
          />
          <AiPromptForm initialPrompt={initialPrompt} busy={busy} onGenerate={generate} />
          <p className="description" style={{marginTop:8}}>
            Enable "Title-Page Cover" on the Layout tab so it appears in the exported PDF.
          </p>
        </div>
      </div>
    </div>
  );
}
